import type { Edge, Node } from "@xyflow/react";
import type { EvaluationDomain, ResolverActionDto, ResolverConfigDto, WhenDto } from "../../../../../types/resolver";
import type { ResolverGraphNodeData } from "./types";
import { graphActionToDtoDetails } from "./actions";
import { sanitizeWhenDto } from "./sanitizeWhenDto";
import { stripNullsDeep } from "./stripNullsDeep";

const DEFAULT_CONFIG_NAME = "Resolver Builder Draft";

function evaluationDomainToOver(domain: EvaluationDomain | undefined): string {
    if (domain === "CASE") return "caseAttributes";
    if (domain === "SPANS" || domain === "LOOSE_SPANS") return "looseSpans";
    return "codes";
}

/** Normalize when so anyMatch/noneMatch use over from the source node's evaluation domain. */
function normalizeWhenForEdge(when: WhenDto | null | undefined, sourceEvaluates: EvaluationDomain | undefined): WhenDto | undefined {
    if (!when) return undefined;
    const over = evaluationDomainToOver(sourceEvaluates);
    if (when.anyMatch) {
        return { ...when, anyMatch: { ...when.anyMatch, over } };
    }
    if (when.noneMatch) {
        return { ...when, noneMatch: { ...when.noneMatch, over } };
    }
    return when;
}

function normalizeNodeId(rawId: string, fallback: number): number {
    const parsed = Number(rawId);
    return Number.isFinite(parsed) ? parsed : fallback;
}

export function toResolverConfigDto(
    nodes: Array<Node<ResolverGraphNodeData>>,
    edges: Edge[],
    configName: string
): ResolverConfigDto {
    const safeEdges = edges ?? [];
    const normalizedNodes = nodes.map((node, index) => {
        const nodeId = normalizeNodeId(node.id, index + 1);
        const outgoing = safeEdges.filter((edge) => edge.source === node.id);
        const actionsList = node.data.actions?.length ? node.data.actions : [];
        const evaluates = node.data.evaluates;
        const actions: ResolverActionDto[] = actionsList.map((graphAction, actionIndex) => {
            const rawWhen = graphAction.rules?.when;
            const sanitizedWhen = rawWhen != null ? sanitizeWhenDto(rawWhen, evaluates) : undefined;
            const compactWhen = sanitizedWhen != null ? stripNullsDeep(sanitizedWhen) : undefined;
            return {
                id: nodeId * 100 + actionIndex,
                action: graphAction.action,
                actionDetails: graphActionToDtoDetails(graphAction),
                rules: compactWhen != null ? { when: compactWhen } : undefined,
            };
        });

        return {
            id: nodeId,
            name: node.data.label || `Node ${nodeId}`,
            evaluates: node.data.evaluates,
            actions,
            edges: outgoing.map((edge, edgeIndex) => {
                const edgeData = edge.data as { when?: WhenDto } | undefined;
                const cleaned = sanitizeWhenDto(edgeData?.when, node.data.evaluates);
                const when = normalizeWhenForEdge(cleaned, node.data.evaluates);
                const compactWhen = when != null ? stripNullsDeep(when) : undefined;
                return {
                    id: nodeId * 1000 + edgeIndex + 1,
                    toNodeId: normalizeNodeId(edge.target, edgeIndex + 1),
                    rules: compactWhen ? { when: compactWhen } : undefined,
                };
            }),
        };
    });

    return {
        resolverVersionMajor: 1,
        name: configName.trim() || DEFAULT_CONFIG_NAME,
        description: "Local resolver graph generated from UI",
        startNodeId: normalizedNodes[0]?.id ?? 1,
        nodes: normalizedNodes,
    };
}
